import camelCase from "lodash/camelCase";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

import { SubscriptionProductModel } from "dash-common";
import {
  Button,
  Flex,
  Form,
  FormErrorTypes,
  Icon,
  Spinner,
  Text,
  ViewerContext,
} from "dash-ui";
import {
  useGetPreviewInvoiceQuery,
  useValidateCouponMutation,
} from "gql/generated.graphql";
import { FormEvent, useContext, useMemo, useState } from "react";
import { CouponData } from "../../AddPaymentPage";
import { ValidCouponIconWrapper } from "./OrderSummaryCard.styles";

/**
 * deprecated
 */
function OrderSummaryCard({
  selectedProduct,
  setCoupon,
  withTrial,
}: {
  selectedProduct: SubscriptionProductModel;
  withTrial: boolean;
  setCoupon?: any;
}) {
  const { userHasTrialed } = useContext(ViewerContext);
  const { t } = useTranslation(["pages", "common"]);
  const [couponCode, setCouponCode] = useState("");
  const [couponData, setCouponData] = useState<CouponData>();
  const theme = useTheme();
  const [validateCoupon] = useValidateCouponMutation();

  const { data: getPreviewInvoiceData, loading: isPreviewInvoiceLoading } =
    useGetPreviewInvoiceQuery({
      skip: !selectedProduct.stripePriceId,
      fetchPolicy: "no-cache",
      variables: {
        input: {
          mode: "upgrade",
          newStripePriceId: selectedProduct.stripePriceId,
          promoCode: couponData?.coupon?.stripeCouponId,
        },
      },
    });

  const previewInvoice = getPreviewInvoiceData?.getPreviewInvoice;
  const trialDays = selectedProduct.trial;

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    validateCoupon({
      variables: {
        args: {
          couponCode: couponCode,
          subscriptionProductId: selectedProduct?._id,
          userHasTrialed: !!userHasTrialed,
        },
      },
      onCompleted: (data) => {
        setCoupon(data.validateCoupon);
        setCouponData(data.validateCoupon);
      },
    });
  };

  const summaryPrice = useMemo(() => {
    if (trialDays && withTrial) {
      return 0;
    }

    if (previewInvoice?.amountDue) {
      // give preference to stripe invoice, if can't fetch, calculate manually.
      return previewInvoice.amountDue;
    }

    if (selectedProduct?.price && couponData?.coupon?.percentOff) {
      return (
        (selectedProduct.price * (100 - couponData.coupon.percentOff)) / 100
      );
    }
    if (selectedProduct?.price && couponData?.coupon?.amountOff) {
      return selectedProduct.price - couponData.coupon.amountOff / 100;
    }
    return selectedProduct?.price || 0;
  }, [selectedProduct, couponData, previewInvoice]);

  return (
    <>
      {!!selectedProduct?.price && (
        <Flex flexDirection="column" gap={8}>
          <Text bold xl>
            Promo Code
          </Text>

          <Form mb={10} onSubmit={onSubmit}>
            <Flex flexDirection="column">
              <Flex flexDirection="row" mt={1}>
                <Form.Input
                  css={"height: 40px;"}
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value?.toUpperCase())}
                  placeholder={
                    t("pages:completeSignUpAddPaymentPage:couponPlaceholder") ||
                    "Enter coupon code"
                  }
                  $errorType={
                    !couponData
                      ? undefined
                      : couponData.isValid
                      ? FormErrorTypes.Valid
                      : FormErrorTypes.Invalid
                  }
                />

                {couponData && (
                  <ValidCouponIconWrapper>
                    <Icon
                      color={
                        couponData.isValid ? theme.colors.g20 : theme.colors.r20
                      }
                      name={couponData.isValid ? "check" : "close"}
                      size={20}
                    />
                  </ValidCouponIconWrapper>
                )}

                <Button type="submit" ml={couponData ? -2 : 3}>
                  Apply
                </Button>
              </Flex>

              {couponData && !couponData.isValid && (
                <Text color={theme.colors.r20}>
                  {couponData.invalidMessageType
                    ? t(
                        `pages:completeSignUpAddPaymentPage:${camelCase(
                          couponData.invalidMessageType
                        )}`
                      )
                    : t("pages:completeSignUpAddPaymentPage:couponError")}
                </Text>
              )}
            </Flex>
          </Form>
        </Flex>
      )}

      <Flex>
        <Text bold xl>
          {t("pages:completeSignUpAddPaymentPage:orderSummaryHeader")}
        </Text>
      </Flex>

      {previewInvoice?.items.map((item) => (
        <Flex key={item.id}>
          <Text>{item.description}:</Text>&nbsp;
          <Text
            semibold
            color={item.amount >= 0 ? theme.colors.b50 : theme.colors.g20}
          >
            {item.amount < 0 && `-`}
            {t(
              "pages:completeSignUpAddPaymentPage:orderSummaryLabel1LinkNoTrial",
              {
                price: Math.abs(item.amount),
              }
            )}
          </Text>
        </Flex>
      ))}

      <Flex mt={4}>
        <Text>
          {t("pages:completeSignUpAddPaymentPage:orderSummaryLabel1")}&nbsp;
        </Text>

        {isPreviewInvoiceLoading ? (
          <Spinner></Spinner>
        ) : (
          selectedProduct && (
            <Text semibold color={theme.colors.b50}>
              {!selectedProduct.price
                ? t("pages:completeSignUpAddPaymentPage:orderSummaryLabel1Free")
                : t(
                    "pages:completeSignUpAddPaymentPage:orderSummaryLabel1LinkNoTrial",
                    {
                      price: summaryPrice?.toFixed(2),
                    }
                  )}
            </Text>
          )
        )}
      </Flex>

      <Flex flexDirection="column" mt={4}>
        {summaryPrice !== 0
          ? couponData &&
            couponData.isValid && (
              <>
                <Text color={theme.colors.g20}>
                  {couponData?.coupon?.description}
                </Text>

                {couponData.coupon?.duration === "forever" && (
                  <Text>
                    {t(
                      "pages:completeSignUpAddPaymentPage:orderSummaryLabel4",
                      {
                        price: summaryPrice?.toFixed(2),
                      }
                    )}
                  </Text>
                )}

                {couponData.coupon?.duration === "once" && (
                  <Text>
                    {t(
                      "pages:completeSignUpAddPaymentPage:orderSummaryLabel4",
                      {
                        price: selectedProduct?.price.toFixed(2),
                      }
                    )}
                  </Text>
                )}
              </>
            )
          : trialDays && (
              <Text>
                {t("pages:completeSignUpAddPaymentPage:orderSummaryLabel2", {
                  countOfDays: trialDays,
                  price: selectedProduct.price,
                })}
              </Text>
            )}
      </Flex>
    </>
  );
}

export default OrderSummaryCard;
